import React from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery } from "gatsby";

import { PlusSmallIcon } from "@heroicons/react/24/solid";

import { classNames } from "@/helpers/classNames";

import productOptionsConfig from "@/data/product-options.config";

import { getFancyPriceString } from "@/components/price-utils";
import MediaWrapper from "@/components/mediaWrapper";
import MarkdownWrapper from "@/components/markdownWrapper";
import LinkWrapper from "@/components/linkWrapper";

export const query = graphql`
  fragment ShopProductListSimpleProductGridProcessedProduct on ShopifyProcessedProduct {
    title
    url
    options {
      name
      values
    }
    featuredMedia {
      ...ShopifyMediaSubquery
    }
    priceRangeV2 {
      minVariantPrice {
        ...ShopifyMoneyV2Subquery
      }
      maxVariantPrice {
        ...ShopifyMoneyV2Subquery
      }
    }
  }

  fragment ShopProductListSimpleProductGridData on StrapiComponentShopProductListSimpleProductGrid {
    title
    topic
    description
    minVariantPriceText
    showCollections {
      string
    }
  }
`;

function processProductCardOptions(products) {
  if (!products?.length) {
    return [];
  }
  return products.map((product) => {
    let colorOption =
      product.options?.find((option) =>
        productOptionsConfig?.colorOptions?.includes(option.name)
      ) || [];
    return { ...product, colorOption };
  });
}

function SimpleProductGrid({ data, products = [] }) {
  if (!data) {
    return <template />;
  }

  if (data.showCollections?.length) {
    const query = useStaticQuery(graphql`
      query shopProductListSimpleProductGrid {
        collections: allShopifyProcessedCollection {
          nodes {
            id
            title
            products {
              ...ShopProductListSimpleProductGridProcessedProduct
            }
          }
        }
      }
    `);

    const collectionTitles =
      data?.showCollections?.map((collection) => collection.string) || [];

    products = query?.collections?.nodes
      ?.filter((collection) =>
        collectionTitles.find((e) => e === collection.title)
      )
      .reduce(
        (products, collection) => [...products, ...collection.products],
        []
      );
  }

  products = processProductCardOptions(products);

  return (
    <div className="bg-background-white">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div>
          {data.topic && (
            <h3 className="block text-base font-semibold uppercase tracking-wide text-text-primary">
              <MarkdownWrapper text={data.topic} />
            </h3>
          )}
          {data.title && (
            <h2 className="text-3xl font-extrabold text-text-primary">
              <MarkdownWrapper text={data.title} />
            </h2>
          )}
          {data.description && (
            <MarkdownWrapper
              text={data.description}
              markdownClassName="mt-4 text-lg text-text-secondary"
              replaceParagraph={false}
            />
          )}
        </div>
        <div className="pt-8">
          <div className="col-span-3">
            <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:gap-x-8 xl:grid-cols-3">
              {Boolean(products?.length) &&
                products.map((product, index) => (
                  <div
                    key={index}
                    className="group relative flex flex-col overflow-hidden rounded-sm border border-border-secondary-light bg-white"
                  >
                    <LinkWrapper to={product.url}>
                      <div className="aspect-[1/1] bg-background-secondary-light group-hover:opacity-75">
                        {product.featuredMedia && (
                          <MediaWrapper
                            media={product.featuredMedia}
                            objectFit="cover"
                            divClassName="aspect-[1/1] h-full"
                          />
                        )}
                      </div>
                      <div className="flex flex-1 flex-col space-y-2 p-4">
                        <h3 className="text-lg font-semibold text-text-primary">
                          {product.title}
                        </h3>
                        <div className="flex flex-1 flex-col justify-end">
                          <p className="text-lg font-medium text-text-primary">
                            {product.priceRangeV2?.minVariantPrice?.amount !==
                              product.priceRangeV2?.maxVariantPrice?.amount &&
                              data.minVariantPriceText && (
                                <span className="pr-1 text-sm">
                                  {data.minVariantPriceText}
                                </span>
                              )}
                            {getFancyPriceString(
                              product.priceRangeV2?.minVariantPrice,
                              "de"
                            )}
                          </p>
                        </div>
                        {Boolean(product?.colorOption?.values?.length) && (
                          <div className="h-10" />
                        )}
                      </div>
                    </LinkWrapper>
                    {Boolean(product?.colorOption?.values?.length) && (
                      <div className="absolute bottom-4 left-4 flex items-center">
                        {[...(product.colorOption.values || [])]
                          .slice(0, 4)
                          .map((color, colorIndex) => (
                            <LinkWrapper
                              key={colorIndex}
                              to={`${product?.url}?${product?.colorOption?.name}=${color}`}
                              className={classNames(
                                "relative mr-2 flex aspect-[1/1] w-10 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border-light-gray ring-offset-2 hover:ring hover:ring-ring-primary-light focus:outline-none"
                              )}
                            >
                              <label className="sr-only">{color}</label>
                              <svg
                                className={classNames(
                                  "absolute inset-0 h-full w-full"
                                )}
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                              >
                                <rect
                                  fill={
                                    Array.isArray(
                                      productOptionsConfig?.colorMap[color]
                                    )
                                      ? productOptionsConfig?.colorMap[color][0]
                                      : productOptionsConfig?.colorMap[color]
                                  }
                                  width="100"
                                  height="100"
                                  x="0"
                                  y="0"
                                />
                                <rect
                                  fill={
                                    Array.isArray(
                                      productOptionsConfig?.colorMap[color]
                                    )
                                      ? productOptionsConfig?.colorMap[color][1]
                                      : productOptionsConfig?.colorMap[color]
                                  }
                                  width="60"
                                  height="120"
                                  x="-8"
                                  y="-65"
                                  transform="rotate(45)"
                                />
                                <rect
                                  fill={
                                    Array.isArray(
                                      productOptionsConfig?.colorMap[color]
                                    )
                                      ? productOptionsConfig?.colorMap[color][2]
                                      : productOptionsConfig?.colorMap[color]
                                  }
                                  width="60"
                                  height="120"
                                  x="88"
                                  y="-65"
                                  transform="rotate(45)"
                                />
                              </svg>
                            </LinkWrapper>
                          ))}
                        {Boolean(product?.colorOption?.values?.length > 4) && (
                          <LinkWrapper
                            to={product?.url}
                            className={classNames(
                              "relative mr-2 flex aspect-[1/1] w-10 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border-light-gray ring-offset-2 hover:ring hover:ring-ring-primary-light focus:outline-none"
                            )}
                          >
                            <label className="sr-only">{"Mehr"}</label>
                            <PlusSmallIcon
                              className={classNames(
                                "absolute inset-0 h-full w-full text-text-secondary"
                              )}
                            />
                          </LinkWrapper>
                        )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SimpleProductGrid.propTypes = {
  data: PropTypes.object,
  products: PropTypes.array,
};

export default SimpleProductGrid;
